import {type ReactNode, useMemo, useState} from "react";
import {formatDateTime, parseInstant, Temporal} from "@/shared/utils/datefunctions";
import type {TeamInsightsActionItemCategory} from "@/shared/timr";
import {useTeamInsights} from "../model/useTeamInsights";
import {RecommendationCard} from "./RecommendationCard";
import {categoryLabel} from "./labels";
import {ACTION_ITEM_CATEGORIES} from "../model/types";

type UserFilter = number | "all";
type CategoryFilter = TeamInsightsActionItemCategory | "all";

export function TeamInsights() {
	const {run, recommendations, loading, error, recalculating, recalculateError, recalculateTimedOut, recalculate, submitFeedback} = useTeamInsights();
	const [userFilter, setUserFilter] = useState<UserFilter>("all");
	const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>("all");

	const employees = useMemo(() => {
		if (!run) {
			return [];
		}
		const byId = new Map<number, string>();
		for (const result of run.results) {
			byId.set(result.userId, result.userName);
		}
		return [...byId.entries()]
			.map(([id, name]) => ({id, name}))
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [run]);

	const visible = useMemo(() => recommendations
		.filter(rec => userFilter === "all" || rec.userId === userFilter)
		.filter(rec => categoryFilter === "all" || rec.item.category === categoryFilter)
		.sort((a, b) => b.item.severity - a.item.severity || a.userName.localeCompare(b.userName)),
	[recommendations, userFilter, categoryFilter]);

	const resetFilters = () => {
		setUserFilter("all");
		setCategoryFilter("all");
	};

	return (
		<div className="teamInsights">
			{renderBanner()}
			{renderControls()}
			{renderResults()}
		</div>
	);

	function renderBanner() {
		const calculatedAt = run ? parseInstant(run.created).toZonedDateTimeISO(Temporal.Now.timeZoneId()) : null;
		return (
			<div className="alert alert-info alert-styled-left teamInsights-banner">
				<div className="teamInsights-bannerText">
					<h6 className="text-semibold no-margin-top">
						<i className="icon-magic-wand position-left"/>
						{messages["reports.teamInsights.banner.heading"]}
						<span className="label label-info teamInsights-badge">{messages["reports.teamInsights.beta"]}</span>
						<span className="label label-default teamInsights-badge">{messages["reports.teamInsights.aiGenerated"]}</span>
					</h6>
					<p className="no-margin-bottom">{messages["reports.teamInsights.banner.text"]}</p>
				</div>
				<div className="teamInsights-bannerActions">
					<div className="text-muted text-size-small">
						<i className="icon-history position-left"/>
						{messages["reports.teamInsights.calculationStatus"]}{" "}
						<span className="text-semibold">
							{formatDateTime(calculatedAt) ?? messages["reports.teamInsights.neverCalculated"]}
						</span>
					</div>
					<button type="button" className="btn btn-primary" onClick={recalculate} disabled={recalculating}>
						{recalculating
							? <><i className="icon-spinner11 spinner position-left"/>{messages["reports.teamInsights.recalculating"]}</>
							: <><i className="icon-sync position-left"/>{messages["reports.teamInsights.recalculate"]}</>}
					</button>
				</div>
			</div>
		);
	}

	function renderControls() {
		return (
			<div className="panel panel-flat">
				<div className="panel-body">
					<div className="row">
						<div className="col-md-6">
							<label className="text-muted text-size-small text-uppercase" htmlFor="teamInsightsUserFilter">
								{messages["reports.teamInsights.filter.employee"]}
							</label>
							<select id="teamInsightsUserFilter" className="form-control" value={String(userFilter)}
									disabled={!run || employees.length === 0}
									onChange={e => setUserFilter(e.target.value === "all" ? "all" : Number(e.target.value))}>
								<option value="all">{messages["reports.teamInsights.filter.allEmployees"]}</option>
								{employees.map(employee => (
									<option key={employee.id} value={employee.id}>{employee.name}</option>
								))}
							</select>
						</div>
						<div className="col-md-6">
							<label className="text-muted text-size-small text-uppercase" htmlFor="teamInsightsCategoryFilter">
								{messages["reports.teamInsights.filter.category"]}
							</label>
							<select id="teamInsightsCategoryFilter" className="form-control" value={categoryFilter}
									disabled={!run}
									onChange={e => setCategoryFilter(e.target.value as CategoryFilter)}>
								<option value="all">{messages["reports.teamInsights.filter.allCategories"]}</option>
								{ACTION_ITEM_CATEGORIES.map(category => (
									<option key={category} value={category}>{categoryLabel(category)}</option>
								))}
							</select>
						</div>
					</div>
				</div>
			</div>
		);
	}

	function renderResults() {
		if (loading) {
			return (
				<PanelMessage>
					<i className="icon-spinner11 spinner position-left"/>{messages["reports.teamInsights.loading"]}
				</PanelMessage>
			);
		}
		if (error) {
			return (
				<div className="alert alert-danger alert-styled-left">
					{messages["reports.teamInsights.loadError"]}
				</div>
			);
		}
		if (recalculating) {
			return (
				<PanelMessage>
					<i className="icon-spinner11 spinner position-left"/>{messages["reports.teamInsights.calculating"]}
				</PanelMessage>
			);
		}
		if (recalculateTimedOut) {
			return (
				<div className="alert alert-warning alert-styled-left">
					{messages["reports.teamInsights.recalculateTimedOut"]}
				</div>
			);
		}
		if (recalculateError) {
			return (
				<div className="alert alert-warning alert-styled-left">
					{messages["reports.teamInsights.recalculateError"]}
				</div>
			);
		}
		if (!run || recommendations.length === 0) {
			return (
				<PanelMessage>
					<p>{messages["reports.teamInsights.emptyState"]}</p>
				</PanelMessage>
			);
		}

		return (
			<div className="teamInsights-results">
				<div className="teamInsights-resultsHeader">
					<h5 className="text-semibold no-margin">{messages["reports.teamInsights.results"]}</h5>
					<span className="text-muted text-size-small">
						{messages.withArguments("reports.teamInsights.resultsCount", visible.length, recommendations.length)}
					</span>
				</div>

				{visible.length === 0 ? (
					<PanelMessage>
						<p className="text-semibold">{messages["reports.teamInsights.noMatch.title"]}</p>
						<p>{messages["reports.teamInsights.noMatch.text"]}</p>
						<button type="button" className="btn btn-default" onClick={resetFilters}>
							{messages["reports.teamInsights.resetFilters"]}
						</button>
					</PanelMessage>
				) : (
					visible.map(rec => <RecommendationCard key={`${rec.resultId}-${rec.item.id}`} recommendation={rec} onFeedback={submitFeedback}/>)
				)}
			</div>
		);
	}
}

function PanelMessage({children}: {children: ReactNode}) {
	return (
		<div className="panel panel-flat">
			<div className="panel-body text-center text-muted">
				{children}
			</div>
		</div>
	);
}
